import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@repo/ui";
import { fileApi } from "~/api";
import { getErrorMessage } from "~/hooks/helpers";
import type { Item } from "~/types/api";
import { getTaskItemsQueryKey } from "./use-task-items";

type UpdateResult = {
  success: boolean;
  successCount: number;
  failedCount: number;
  skippedCount: number;
  errors: Array<{ itemId: number; reason: string }>;
};

type SingleItemMutationContext = {
  previousItems: Item[] | undefined;
  taskId: number;
  itemId: number;
  queryKey: readonly unknown[];
  previousStatusCode: number | undefined;
};

type Props = {
  updateItems: (items: Item[], itemIds: Set<number>, statusCode: number) => Promise<boolean>;
  isSubmitting: boolean;
  error: string | null;
};

export const useUpdateItem = (): Props => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const mutation = fileApi.useMutation("put", "/api/task/{taskId}/item/{itemId}", {
    onMutate: async (variables): Promise<SingleItemMutationContext> => {
      const { taskId, itemId } = variables.params.path;
      const newStatusCode = variables.body.statusCode;

      const queryKey = getTaskItemsQueryKey(taskId);
      await queryClient.cancelQueries({ queryKey });

      const previousItems = queryClient.getQueryData<Item[]>(queryKey);
      const previousItem = previousItems?.find((item) => item.id === itemId);
      const previousStatusCode = previousItem?.statusCode;

      // Optimistic update
      if (previousItems) {
        queryClient.setQueryData<Item[]>(queryKey, (oldItems) => {
          if (!oldItems) return oldItems;

          return oldItems.map((item) =>
            item.id === itemId ? { ...item, statusCode: newStatusCode } : item
          );
        });
      }

      return { previousItems, taskId, itemId, queryKey, previousStatusCode };
    },
    onError: (_err, _variables, context) => {
      const ctx = context as SingleItemMutationContext | undefined;
      if (ctx?.previousItems && ctx?.queryKey && ctx?.previousStatusCode !== undefined) {
        queryClient.setQueryData<Item[]>(ctx.queryKey, (currentItems) => {
          if (!currentItems) return currentItems;

          return currentItems.map((item) =>
            item.id === ctx.itemId ? { ...item, statusCode: ctx.previousStatusCode! } : item
          );
        });
      }
    },

    onSettled: (_data, _error, variables) => {
      const { taskId } = variables.params.path;
      queryClient.invalidateQueries({
        queryKey: getTaskItemsQueryKey(taskId),
      });
    },
  });

  const updateItems = async (
    items: Item[],
    itemIds: Set<number>,
    statusCode: number
  ): Promise<boolean> => {
    const result: UpdateResult = {
      success: false,
      successCount: 0,
      failedCount: 0,
      skippedCount: 0,
      errors: [],
    };

    const itemsByTask = new Map<number, Item[]>();
    try {
      const updatePromises = Array.from(itemIds).map(async (itemId) => {
        const item = items.find((i) => i.id === itemId);
        if (!item) {
          result.skippedCount++;
          result.errors.push({
            itemId,
            reason: "Item not found in provided items list",
          });
          console.warn(`Item ${itemId} not found in items list`);
          return { itemId, status: "skipped" as const };
        }

        if (!item.taskId) {
          result.skippedCount++;
          result.errors.push({
            itemId,
            reason: "Item has no taskId",
          });
          console.warn(`Item ${itemId} has no taskId, skipping update`);
          return { itemId, status: "skipped" as const };
        }

        if (!itemsByTask.has(item.taskId)) {
          const queryKey = getTaskItemsQueryKey(item.taskId);
          const taskItems = queryClient.getQueryData<Item[]>(queryKey);
          if (taskItems) {
            itemsByTask.set(item.taskId, [...taskItems]);
          }
        }

        try {
          await mutation.mutateAsync({
            params: {
              path: { taskId: item.taskId, itemId },
            },
            body: {
              taskId: item.taskId,
              name: item.name,
              path: item.path,
              statusCode: statusCode,
              taskItemFields: item.taskItemFields || null,
              fileContentHash: item.fileContentHash ?? null, //TODO: Placeholder for Future Fix...
            },
          });

          result.successCount++;
          return { itemId, status: "success" as const };
        } catch (err) {
          result.failedCount++;
          const errorMessage = getErrorMessage(err, "Unknown error");
          result.errors.push({ itemId, reason: errorMessage });
          console.error(`Failed to update item ${itemId}:`, errorMessage);
          return { itemId, status: "failed" as const };
        }
      });

      await Promise.all(updatePromises);

      result.success = result.failedCount === 0 && result.skippedCount === 0;

      if (result.success) {
        showToast(
          "success",
          `Successfully updated ${result.successCount} item${result.successCount === 1 ? "" : "s"}`
        );
      } else if (result.successCount > 0) {
        showToast(
          "warning",
          `Partially updated items`,
          `${result.successCount} succeeded, ${result.failedCount} failed, ${result.skippedCount} skipped`
        );
      } else {
        showToast(
          "error",
          "Failed to update items",
          `${result.failedCount} failed, ${result.skippedCount} skipped`
        );
      }

      return result.success;
    } catch (err) {
      const error = getErrorMessage(
        err,
        "Failed to update item statuses due to an unexpected error"
      );
      itemsByTask.forEach((taskItems, taskId) => {
        const queryKey = getTaskItemsQueryKey(taskId);
        queryClient.setQueryData(queryKey, taskItems);
      });
      showToast("error", error, "Failed to update item statuses");
      console.error("Failed to update item statuses:", error);
      return false;
    }
  };

  return {
    updateItems,
    isSubmitting: mutation.isPending,
    error: mutation.error
      ? getErrorMessage(mutation.error, "An error occurred while updating items")
      : null,
  };
};
